'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RefillItem {
  id: string;
  medicationName: string;
  refillDate: string;
  daysUntilRefill: number;
  pharmacy: string;
  pharmacyPhone: string;
  refillsRemaining: number;
  status: 'urgent' | 'upcoming' | 'scheduled';
}

interface RefillTrackerProps {
  refills: RefillItem[];
  onRequestRefill: (id: string) => void;
}

export default function RefillTracker({
  refills,
  onRequestRefill,
}: RefillTrackerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'urgent':
        return 'bg-error/10 text-error border-error/20';
      case 'upcoming':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'scheduled':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-foreground border-border';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'urgent':
        return 'Urgent';
      case 'upcoming':
        return 'Upcoming';
      case 'scheduled':
        return 'Scheduled';
      default:
        return 'Unknown';
    }
  };

  const urgentCount = refills.filter((r) => r.status === 'urgent').length;

  return (
    <div className="glass rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 lg:p-6 flex items-center justify-between hover:bg-muted/50 transition-smooth"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="ArrowPathIcon" size={24} className="text-accent" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Refill Tracker
            </h3>
            <p className="text-sm font-caption text-muted-foreground">
              {refills.length} refill{refills.length > 1 ? 's' : ''} tracked
              {urgentCount > 0 && (
                <span className="text-error"> • {urgentCount} urgent</span>
              )}
            </p>
          </div>
        </div>
        <Icon
          name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={20}
          className="text-muted-foreground"
        />
      </button>

      {isExpanded && (
        <div className="border-t border-sage-200/30 p-4 lg:p-6 space-y-4 animate-slide-in-up">
          {refills.map((refill) => (
            <div
              key={refill.id}
              className="p-4 rounded-lg border border-sage-200/30 hover:shadow-warm-sm transition-smooth"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-caption font-semibold text-foreground mb-1">
                    {refill.medicationName}
                  </h4>
                  <p className="text-sm font-caption text-muted-foreground">
                    {refill.pharmacy}
                  </p>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-caption font-medium border ${getStatusStyles(
                    refill.status
                  )}`}
                >
                  {getStatusLabel(refill.status)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Refill Date
                  </p>
                  <p className="text-sm font-caption font-medium text-foreground">
                    {refill.refillDate}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Days Until Refill
                  </p>
                  <p className="text-sm font-caption font-medium text-foreground">
                    {refill.daysUntilRefill} days
                  </p>
                </div>
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Refills Remaining
                  </p>
                  <p className="text-sm font-caption font-medium text-foreground">
                    {refill.refillsRemaining}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Pharmacy Phone
                  </p>
                  <a
                    href={`tel:${refill.pharmacyPhone}`}
                    className="text-sm font-caption font-medium text-primary hover:text-primary/80 transition-smooth"
                  >
                    {refill.pharmacyPhone}
                  </a>
                </div>
              </div>

              <button
                onClick={() => onRequestRefill(refill.id)}
                className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-caption font-medium hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
              >
                <Icon name="PhoneIcon" size={16} />
                Request Refill
              </button>
            </div>
          ))}

          {refills.length === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-sage-50 dark:bg-sage-900/20 flex items-center justify-center mx-auto mb-3">
                <Icon
                  name="CheckCircleIcon"
                  size={32}
                  className="text-success"
                  variant="solid"
                />
              </div>
              <p className="text-base font-caption font-medium text-foreground mb-1">
                All Refills Up to Date
              </p>
              <p className="text-sm font-caption text-muted-foreground">
                No refills needed at this time
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}